import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  CollectionPreferences,
  CollectionPreferencesProps,
  Pagination,
  PropertyFilter,
  Table,
} from '@amzn/awsui-components-react-v3';
import { defaultWrapLinesPreference, largePageSizePreference, paginationLabels } from 'src/commons/tables';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { TableProps } from '@amzn/awsui-components-react-v3/polaris/table/interfaces';
import { listDataSets } from 'src/api/catalog';
import {
  DATA_LOAD_LIMIT,
  DZ_GLUE_DATASOURCE_ID,
  DZ_REDSHIFT_DATASOURCE_ID,
  GALAXI_DATASOURCE_ID,
  LAKE_FORMATION_DATASOURCE_ID,
  REDSHIFT_DATASOURCE_ID,
  TABLE_CONTENT_TYPE,
} from 'src/commons/constants';
import { isValidGalaxiGroup, isValidWorkspace } from 'src/commons/validationUtils';
import {
  createCatalogDetailLink,
  createDatabaseDetailLink,
  createDatasetDetailLink,
  createGroupDetailLink,
  createWorkspaceDetailLink,
} from 'src/routes';
import { typeToHumanReadableObject } from 'src/components/permissions/myDatasets/common';
import {
  DefaultRouteProps,
  getGroupOrWorkspaceName,
  getHCResourcePagePropertyFilter,
  isSearchEnabled,
  keyForCatalogNameMap,
} from 'src/commons/common';
import { getGalaxiGroupURL } from 'src/components/workspaces/common/common';
import { EmptyState } from 'src/commons/EmptyState';
import { PageHeader } from 'src/components/common/PageHeader';
import { Link } from 'src/components/common/Link';

export interface WSBrowseDatasetsProps extends DefaultRouteProps {
  setContentType: any;
  activeGroup: string;
  username: string;
  activeWorkspace: any;
  catalogMap: any;
}

export const WSBrowseDatasets = (props: WSBrowseDatasetsProps) => {
  const [allItems, setItems] = useState([]);
  const [loadingTable, setLoadingTable] = useState(false);
  const [allDataLoaded, setAllDataLoaded] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [nextToken, setNextToken] = useState(null);
  const handleRefresh = async () => {
    setLoadingTable(true);
    const dataSets = await listDataSets({ Limit: DATA_LOAD_LIMIT, IncludeTotalCount: true });
    setTotalCount(dataSets.TotalCount);
    setNextToken(dataSets.NextToken);
    if (dataSets.NextToken == null) {
      setAllDataLoaded(true);
    }
    let datasetList = dataSets.DataSetList;
    let nonPrivateDataSets = [];
    let privateDataSets = [];
    for (let dataset of datasetList) {
      let catalogName = props.catalogMap.get(
        keyForCatalogNameMap({
          dataSource: dataset?.IdInfo?.DataSourceId,
          catalogId: dataset?.IdInfo?.CatalogId,
          clusterId: dataset?.IdInfo?.ClusterIdentifier,
          redshiftWorkgroupName: dataset?.IdInfo?.RedshiftWorkgroupName,
          bucketName: dataset?.IdInfo?.BucketName,
          region: dataset?.IdInfo?.Region,
        }),
      );
      dataset['CatalogName'] = catalogName == null ? dataset?.IdInfo?.CatalogId : catalogName;
      if (dataset.DataClassification === undefined || dataset.DataClassification != 'Private') {
        nonPrivateDataSets.push(dataset);
      } else {
        if (dataset.Owners.includes(props.activeWorkspace.workspaceId)) {
          privateDataSets.push(dataset);
        }
      }
    }
    datasetList = nonPrivateDataSets.concat(privateDataSets);
    setItems(datasetList);
    setLoadingTable(false);
  };

  const loadMoreData = async () => {
    setLoadingTable(true);
    const dataSets = await listDataSets({ NextToken: nextToken, Limit: DATA_LOAD_LIMIT });
    setNextToken(dataSets.NextToken);
    let nonPrivateDataSets = [];
    let privateDataSets = [];
    let datasetList = dataSets.DataSetList;
    for (let dataset of datasetList) {
      if (dataset?.IdInfo?.ClusterIdentifier != null) {
        let catalogName = props.catalogMap.get(
          dataset?.IdInfo?.CatalogId + ':' + dataset?.IdInfo?.ClusterIdentifier + ':' + dataset?.IdInfo?.Region,
        );
        dataset['CatalogName'] = catalogName == null ? dataset?.IdInfo?.CatalogId : catalogName;
      } else {
        let catalogName = props.catalogMap.get(dataset?.IdInfo?.CatalogId + ':' + dataset?.IdInfo?.Region);
        dataset['CatalogName'] = catalogName == null ? dataset?.IdInfo?.CatalogId : catalogName;
      }
      if (dataset.DataClassification === undefined || dataset.DataClassification != 'Private') {
        nonPrivateDataSets.push(dataset);
      } else {
        if (dataset.Owners.includes(props.activeWorkspace.workspaceId)) {
          privateDataSets.push(dataset);
        }
      }
    }
    datasetList = nonPrivateDataSets.concat(privateDataSets);
    let currentItems = allItems;
    currentItems.push(...datasetList);
    setItems(currentItems);
    if (dataSets.NextToken == null) {
      setAllDataLoaded(true);
    }
    setLoadingTable(false);
  };

  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);
    handleRefresh();
  }, []);

  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 25,
  });

  const flatten = (items) => {
    let list = [];
    for (let item of items) {
      if (
        item.DataSourceType == LAKE_FORMATION_DATASOURCE_ID ||
        item.DataSourceType == DZ_GLUE_DATASOURCE_ID ||
        item.DataSourceType == GALAXI_DATASOURCE_ID
      ) {
        list.push({
          datasetName: item.DataSetName,
          databaseName: item.IdInfo?.DatabaseName,
          catalogId: item.IdInfo?.CatalogId,
          bucketName: item.IdInfo?.BucketName,
          catalogName: item.CatalogName,
          Id: item.Id,
          owner: item.Owners[0],
          region: item.IdInfo?.Region,
          datasourceType: item.DataSourceType,
          dataSourceId: item.DataSourceType,
          type: typeToHumanReadableObject[LAKE_FORMATION_DATASOURCE_ID],
        });
      } else if (item.DataSourceType == REDSHIFT_DATASOURCE_ID || item.DataSourceType == DZ_REDSHIFT_DATASOURCE_ID) {
        list.push({
          datasetName: item.DataSetName,
          databaseName: item.IdInfo?.DatabaseName,
          catalogId: item.IdInfo?.CatalogId,
          bucketName: item.IdInfo?.BucketName,
          catalogName: item.CatalogName,
          Id: item.Id,
          owner: item.Owners[0],
          region: item.IdInfo?.Region,
          schemaName: item.IdInfo.SchemaName,
          clusterIdentifier: item.ClusterIdentifier,
          datasourceType: item.DataSourceType,
          dataSourceId: item.DataSourceType,
          type: typeToHumanReadableObject[item.DataSourceType],
        });
      }
    }
    return list;
  };

  const columnDefinitions: TableProps.ColumnDefinition<any>[] = [
    {
      id: 'datasetName',
      header: 'Dataset name',
      cell: (item) => <Link href={createDatasetDetailLink(item?.Id)}>{item.datasetName}</Link>,
      sortingField: 'datasetName',
      minWidth: 200,
    },
    {
      id: 'databaseName',
      header: 'Database name',
      cell: (item) => (
        <Link
          href={createDatabaseDetailLink(
            item?.datasourceType,
            item?.catalogId,
            item?.clusterIdentifier,
            item?.redshiftWorkgroupName,
            item?.bucketName,
            item?.databaseName,
            item?.region,
          )}
        >
          {item.databaseName}
        </Link>
      ),
      sortingField: 'databaseName',
      minWidth: 200,
    },
    {
      id: 'catalogName',
      header: 'Catalog name',
      cell: (item) => (
        <Link
          href={createCatalogDetailLink(
            item?.dataSourceId,
            item?.catalogId,
            item?.clusterIdentifier,
            item?.workgroupName,
            item?.bucketName,
            item?.region,
          )}
        >
          {item.catalogName}
        </Link>
      ),
      sortingField: 'catalogName',
      minWidth: 200,
    },
    {
      id: 'owner',
      header: 'Owner',
      cell: (item) =>
        isValidGalaxiGroup(item.owner) ? (
          <Link href={getGalaxiGroupURL(getGroupOrWorkspaceName(item.owner, props.workspaceNameMap))}>
            {getGroupOrWorkspaceName(item.owner, props.workspaceNameMap)}
          </Link>
        ) : (
          <Link
            href={
              isValidWorkspace(item.owner) ? createWorkspaceDetailLink(item.owner) : createGroupDetailLink(item.owner)
            }
          >
            {getGroupOrWorkspaceName(item.owner, props.workspaceNameMap)}
          </Link>
        ),
      sortingField: 'owner',
      minWidth: 200,
    },
    {
      id: 'type',
      header: 'Type',
      cell: (item) => item.type,
      minWidth: 100,
    },
  ];

  const { items, collectionProps, paginationProps, propertyFilterProps, filteredItemsCount } = useCollection(
    flatten(allItems),
    {
      filtering: {
        noMatch: <EmptyState title={'No matches'} subtitle={"We can't find a match."} />,
        empty: <EmptyState title={'No datasets'} subtitle={'No datasets were found in the catalog.'} />,
      },
      pagination: {
        pageSize: preferences.pageSize,
      },
      sorting: {},
      selection: {},
      propertyFiltering: {
        filteringProperties: [
          {
            propertyLabel: 'Catalog name',
            key: 'catalogName',
            groupValuesLabel: 'Catalog names',
          },
          {
            propertyLabel: 'Database name',
            key: 'databaseName',
            groupValuesLabel: 'Database names',
          },
          {
            propertyLabel: 'Schema name',
            key: 'schemaName',
            groupValuesLabel: 'Schema names',
          },
          {
            propertyLabel: 'Dataset name',
            key: 'datasetName',
            groupValuesLabel: 'Dataset names',
          },
          {
            propertyLabel: 'Owner',
            key: 'owner',
            groupValuesLabel: 'Owners',
          },
          {
            propertyLabel: 'Type',
            key: 'type',
            groupValuesLabel: 'Types',
          },
        ],
      },
    },
  );

  const handlePageChange = async (e) => {
    if (paginationProps.currentPageIndex == paginationProps.pagesCount && !allDataLoaded) {
      await loadMoreData();
    }
    if (
      paginationProps.currentPageIndex != paginationProps.pagesCount ||
      (e.detail.currentPageIndex <= paginationProps.pagesCount &&
        e.detail.currentPageIndex != paginationProps.currentPageIndex)
    ) {
      paginationProps.onChange(e);
    }
  };

  return (
    <>
      <Table
        {...collectionProps}
        loadingText='Loading datasets...'
        loading={loadingTable}
        columnDefinitions={columnDefinitions}
        items={items}
        wrapLines={preferences.wrapLines}
        resizableColumns={true}
        header={<PageHeader buttons={[]} counter={`(${totalCount})`} header={'Datasets'} />}
        pagination={
          <Pagination
            {...paginationProps}
            ariaLabels={paginationLabels}
            onChange={(e) => handlePageChange(e)}
            // currentPageIndex={currentPageIndex}
            openEnd={!allDataLoaded}
            disabled={loadingTable}
          />
        }
        preferences={
          <CollectionPreferences
            title={'Preferences'}
            confirmLabel={'Confirm'}
            cancelLabel={'Cancel'}
            preferences={preferences}
            onConfirm={({ detail }) => setPreferences(detail)}
            pageSizePreference={largePageSizePreference}
            wrapLinesPreference={defaultWrapLinesPreference}
          />
        }
        filter={
          <PropertyFilter
            {...propertyFilterProps}
            disabled={isSearchEnabled()}
            i18nStrings={getHCResourcePagePropertyFilter()}
            countText={`${filteredItemsCount} ${filteredItemsCount === 1 ? 'match' : 'matches'}`}
          />
        }
      />
    </>
  );
};
